import { Matrix, Row, Section } from "anketa-core/group";
import { LikertScale, Translation } from "anketa-core/i18n";
import { QuestionsRightsTypes } from "..";
import {
  AnketaCheckPermissionOptions,
  AnketaResourcesEnum,
  getYesNoAsBoolean,
} from "../../../../";
import { getTranslation } from "src/internationalization";
import { ComplexQuestion } from "anketa-core/complex_question";
import { ComplexFactory } from "anketa-core/base";
import { User } from "anketa-core/complex";
import { LikertQuestion } from "anketa-core/likert_question";
import { DateQuestion } from "anketa-core/date_question";
import { CustomerContact } from "./customerContact";
import { OpsanioCreateContactDefaults } from "../../../";

ComplexFactory.registerClass({
  name: "KeycloakUserSales",
  Constructor: User as new (init?: any) => User,
  form: User.getForm,
});
ComplexFactory.registerClass({
  name: "KeycloakUserTechnical",
  Constructor: User as new (init?: any) => User,
  form: User.getForm,
});
ComplexFactory.registerClass({
  name: "KeycloakUserCustomer",
  Constructor: User as new (init?: any) => User,
  form: User.getForm,
});
ComplexFactory.registerClass({
  name: "CustomerContact",
  Constructor: CustomerContact as new (init?: any) => CustomerContact,
  form: CustomerContact.getForm,
});

export const generalInformationForm = (
  questionsRights: (
    q: QuestionsRightsTypes,
    options?: AnketaCheckPermissionOptions<AnketaResourcesEnum.questionnaires>,
  ) => void,
  /**
   * Contacts captured in the create wizard. When provided (creation only), the
   * matching contact fields are pre-filled: opsanio sales/technical as
   * comma-joined Keycloak user ids, customer roles as comma-joined e-mails.
   * Empty lists are skipped so nothing is overwritten with a blank value.
   */
  contactDefaults?: OpsanioCreateContactDefaults,
): Section => {
  const general = new Section("general", [
    new Translation(
      "en",
      getTranslation("en", "anketa.documentation.opsanio.general"),
    ),
    new Translation(
      "de",
      getTranslation("de", "anketa.documentation.opsanio.general"),
    ),
  ]);

  const opsanioContact = new Row("contacts", [
    new Translation(
      "en",
      getTranslation("en", "anketa.documentation.opsanio.general.contact"),
    ),
    new Translation(
      "de",
      getTranslation("de", "anketa.documentation.opsanio.general.contact"),
    ),
  ]);
  general.addChild(opsanioContact);

  const salesContact = new ComplexQuestion(
    "sales_contact",
    [
      new Translation(
        "en",
        getTranslation("en", "anketa.documentation.opsanio.general.sales"),
      ),
      new Translation(
        "de",
        getTranslation("de", "anketa.documentation.opsanio.general.sales"),
      ),
    ],
    "KeycloakUserSales",
  );
  questionsRights(salesContact, {
    isInternal: true,
    isExternal: false,
  });
  opsanioContact.addChild(salesContact);

  const technicalContact = new ComplexQuestion(
    "technical_contact",
    [
      new Translation(
        "en",
        getTranslation(
          "en",
          "anketa.documentation.opsanio.general.technical_contact",
        ),
      ),
      new Translation(
        "de",
        getTranslation(
          "de",
          "anketa.documentation.opsanio.general.technical_contact",
        ),
      ),
    ],
    "KeycloakUserTechnical",
  );
  questionsRights(technicalContact, {
    isInternal: true,
    isExternal: false,
  });
  opsanioContact.addChild(technicalContact);

  const opsanioContactCustomers = new Row("customers", [
    new Translation(
      "en",
      getTranslation(
        "en",
        "anketa.documentation.opsanio.general.contact.customer",
      ),
    ),
    new Translation(
      "de",
      getTranslation(
        "de",
        "anketa.documentation.opsanio.general.contact.customer",
      ),
    ),
  ]);

  // Customer-side contacts, one per role (Vertrieb / Technisch / Organisatorisch).
  // Each uses the customer Keycloak picker; on save, newly typed e-mail
  // addresses are provisioned as managed users in the customer organization
  // (see functions/questionnaires/catalog-action provisioning).
  const customerContactRoles: {
    id: string;
    labelEn: string;
    labelDe: string;
  }[] = [
    {
      id: "customer_sales_contact",
      labelEn: "Customer - Sales contact",
      labelDe: "Kunde - Vertriebskontakt",
    },
    {
      id: "customer_technical_contact",
      labelEn: "Customer - Technical contact",
      labelDe: "Kunde - Technischer Kontakt",
    },
    {
      id: "customer_organizational_contact",
      labelEn: "Customer - Organizational contact",
      labelDe: "Kunde - Organisatorischer Kontakt",
    },
  ];
  const customerHelp = [
    new Translation(
      "en",
      getTranslation(
        "en",
        "anketa.documentation.opsanio.general.contact.customer.help",
      ),
    ),
    new Translation(
      "de",
      getTranslation(
        "de",
        "anketa.documentation.opsanio.general.contact.customer.help",
      ),
    ),
  ];
  const customerContactByRole = new Map<string, ComplexQuestion>();
  customerContactRoles.forEach((role) => {
    const customerContact = new ComplexQuestion(
      role.id,
      [
        new Translation("en", role.labelEn),
        new Translation("de", role.labelDe),
      ],
      "CustomerContact",
    );
    customerContact.description = customerHelp;
    questionsRights(customerContact, {
      isInternal: true,
      isExternal: true,
    });
    opsanioContactCustomers.addChild(customerContact);
    customerContactByRole.set(role.id, customerContact);
  });

  // Dedicated "notify on update" recipients. Multi-value combo box
  // (UpdateRecipientsEditor): any number of addresses, suggested from the
  // customer Keycloak users but freely typeable/pasteable. Unlike the
  // per-role customer contacts above, this field's content key is NOT listed in
  // CUSTOMER_CONTACT_KEYS (functions/.../catalog-action/contacts.ts), so saving
  // it provisions NO Keycloak users - the addresses are used purely as the
  // recipient list for wave update notifications (functions/gitlab/merges.ts).
  const updateNotificationContacts = new ComplexQuestion(
    "update_notification_contacts",
    [
      new Translation("en", "Customer - Update notification recipients"),
      new Translation("de", "Kunde - Update-Benachrichtigungsempfänger"),
    ],
    "KeycloakUserCustomer",
  );
  updateNotificationContacts.description = [
    new Translation(
      "en",
      "E-mail addresses notified when an update is rolled out. Add as many recipients as you need - pick existing customer contacts, or type/paste any e-mail address; no Keycloak users are created. All of them are addressed on the update mail and its calendar invite.",
    ),
    new Translation(
      "de",
      "E-Mail-Adressen, die bei einem Update benachrichtigt werden. Beliebig viele Empfänger möglich - bestehende Kundenkontakte auswählen oder beliebige E-Mail-Adressen eingeben bzw. einfügen; es werden keine Keycloak-Benutzer angelegt. Alle Empfänger stehen im Update-Mail und in der Kalendereinladung.",
    ),
  ];
  questionsRights(updateNotificationContacts, {
    isInternal: true,
    isExternal: true,
  });
  opsanioContactCustomers.addChild(updateNotificationContacts);

  general.addChild(opsanioContactCustomers);

  const information = new Matrix("information", [
    new Translation("en", "Information"),
    new Translation("de", "Informationen"),
  ]);

  const ref = new LikertQuestion(
    "ref",
    [
      new Translation("en", "May we publicly name you as a reference?"),
      new Translation("de", "Dürfen wir Sie öffentlich als Referenz nennen?"),
    ],
    LikertScale.yes_no,
  );
  information.addChild(ref);

  const poc = new LikertQuestion(
    "poc",
    [
      new Translation("en", "Want a proof of concept?"),
      new Translation("de", "Möchtet einen Proof of Concept?"),
    ],
    LikertScale.yes_no,
  );
  const pocSuccess = new LikertQuestion(
    "poc_success",
    [
      new Translation("en", "Was the PoC successful?"),
      new Translation("de", "War der PoC erfolgreich?"),
    ],
    LikertScale.yes_no,
  );
  information.addChild(poc);

  const dates = new Row("date", [
    new Translation("en", "Date information"),
    new Translation("de", "Datum-Informationen"),
  ]);
  const dateStart = new DateQuestion("date_start", [
    new Translation("en", "Start date"),
    new Translation("de", "Start Datum"),
  ]);
  dates.addChild(dateStart);
  const dateEnd = new DateQuestion("date_end", [
    new Translation("en", "End date"),
    new Translation("de", "End Datum"),
  ]);
  dates.addChild(dateEnd);

  poc.addChangeListener(() => {
    const valueRaw = poc.fact.raw;
    const value = getYesNoAsBoolean(valueRaw as string);
    if (value) {
      if (!information.containsChild(pocSuccess)) {
        information.addChild(pocSuccess);
      }
    } else {
      if (information.containsChild(pocSuccess)) {
        information.removeChild(pocSuccess);
      }
    }
  });

  general.addChild(dates);
  general.addChild(information);

  // Seed the contact fields from wizard defaults (creation only). Each field's
  // fact value is a comma-joined string, matching what the in-form editors
  // store and what the parser (getOpsanioContact) / backend provisioning read:
  //   sales/technical  -> Keycloak user ids
  //   customer roles   -> e-mail addresses
  // Empty selections are skipped, so no field is cleared with a blank value.
  if (contactDefaults) {
    const seed = (question: ComplexQuestion, values: string[]) => {
      const joined = values.map((v) => v.trim()).filter(Boolean).join(",");
      if (joined) {
        question.setFactValue(joined);
      }
    };
    seed(salesContact, contactDefaults.sales);
    seed(technicalContact, contactDefaults.technical);
    const customerSales = customerContactByRole.get("customer_sales_contact");
    if (customerSales) seed(customerSales, contactDefaults.customerSales);
    const customerTechnical = customerContactByRole.get(
      "customer_technical_contact",
    );
    if (customerTechnical)
      seed(customerTechnical, contactDefaults.customerTechnical);
    const customerOrganizational = customerContactByRole.get(
      "customer_organizational_contact",
    );
    if (customerOrganizational)
      seed(customerOrganizational, contactDefaults.customerOrganizational);
  }

  return general;
};
